import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import './FeedbackPage.css';

const FeedbackPage = () => {
  // State management
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resolveId, setResolveId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    type: 'feedback',
    category: 'academics',
    subject: '',
    description: '',
    priority: 'normal',
  });

  const typeColors = {
    feedback: '#3b82f6',
    complaint: '#dc2626',
    suggestion: '#f59e0b',
    appreciation: '#10b981',
  };

  const typeBgColors = {
    feedback: '#dbeafe',
    complaint: '#fee2e2',
    suggestion: '#fef3c7',
    appreciation: '#d1fae5',
  };

  const priorityColors = {
    urgent: '#dc2626',
    high: '#f472b6',
    normal: '#3b82f6',
    low: '#6b7280',
  };

  const priorityBgColors = {
    urgent: '#fee2e2',
    high: '#fbcfe8',
    normal: '#dbeafe',
    low: '#f3f4f6',
  };

  const statusColors = {
    open: '#f472b6',
    'in-progress': '#3b82f6',
    resolved: '#10b981',
    closed: '#6b7280',
  };

  const statusBgColors = {
    open: '#fbcfe8',
    'in-progress': '#dbeafe',
    resolved: '#d1fae5',
    closed: '#f3f4f6',
  };

  // Fetch feedbacks and stats
  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/feedbacks');
      const data = response.data.items || response.data;
      setFeedbacks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching feedbacks:', err);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/feedbacks/stats');
      setStats(response.data || { open: 0, inProgress: 0, resolved: 0, closed: 0 });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleAddFeedback = () => {
    setEditingId(null);
    setFormData({
      type: 'feedback',
      category: 'academics',
      subject: '',
      description: '',
      priority: 'normal',
    });
    setShowModal(true);
  };

  const handleEditFeedback = (feedback) => {
    setEditingId(feedback.id);
    setFormData({
      type: feedback.type || 'feedback',
      category: feedback.category || 'academics',
      subject: feedback.subject || '',
      description: feedback.description || '',
      priority: feedback.priority || 'normal',
    });
    setShowModal(true);
  };

  const handleDeleteFeedback = async (feedbackId) => {
    try {
      await api.delete(`/feedbacks/${feedbackId}`);
      setFeedbacks(feedbacks.filter((f) => f.id !== feedbackId));
      setDeleteConfirm(null);
      fetchStats();
    } catch (err) {
      console.error('Error deleting feedback:', err);
      setError('Failed to delete feedback');
    }
  };

  const handleResolve = async () => {
    if (!resolveId) return;
    try {
      await api.put(`/feedbacks/${resolveId}/resolve`, { resolutionNotes });
      setResolveId(null);
      setResolutionNotes('');
      fetchFeedbacks();
      fetchStats();
    } catch (err) {
      console.error('Error resolving feedback:', err);
      setError('Failed to resolve feedback');
    }
  };

  const handleSaveFeedback = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData };

      if (editingId) {
        await api.put(`/feedbacks/${editingId}`, payload);
      } else {
        await api.post('/feedbacks', payload);
      }

      setShowModal(false);
      fetchFeedbacks();
      fetchStats();
    } catch (err) {
      console.error('Error saving feedback:', err);
      setError('Failed to save feedback');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredFeedbacks = feedbacks.filter((feedback) => {
    const typeMatch = typeFilter === 'all' || feedback.type === typeFilter;
    const statusMatch = statusFilter === 'all' || feedback.status === statusFilter;
    const priorityMatch = priorityFilter === 'all' || feedback.priority === priorityFilter;
    const categoryMatch = categoryFilter === 'all' || feedback.category === categoryFilter;
    return typeMatch && statusMatch && priorityMatch && categoryMatch;
  });

  const StatCard = ({ icon: Icon, label, value, bgColor }) => (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          backgroundColor: bgColor,
          width: '48px',
          height: '48px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={24} style={{ color: '#ffffff' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
          {label}
        </p>
        <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
          {value}
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Feedback & Complaints
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage feedback, complaints, and suggestions from community</p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <StatCard icon={Clock} label="Open" value={stats.open} bgColor="#f472b6" />
          <StatCard icon={Clock} label="In Progress" value={stats.inProgress} bgColor="#3b82f6" />
          <StatCard icon={CheckCircle} label="Resolved" value={stats.resolved} bgColor="#10b981" />
          <StatCard icon={MessageSquare} label="Closed" value={stats.closed} bgColor="#6b7280" />
        </div>

        {/* Filter Bar */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Types</option>
            <option value="feedback">Feedback</option>
            <option value="complaint">Complaint</option>
            <option value="suggestion">Suggestion</option>
            <option value="appreciation">Appreciation</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Categories</option>
            <option value="academics">Academics</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="staff">Staff</option>
            <option value="food">Food & Nutrition</option>
            <option value="transport">Transport</option>
            <option value="other">Other</option>
          </select>

          <button
            onClick={handleAddFeedback}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: 'auto',
            }}
          >
            <Plus size={18} />
            Submit Feedback
          </button>
        </div>

        {/* Feedback Table */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading feedback...
            </div>
          ) : filteredFeedbacks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
              <MessageSquare size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No feedback found</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {typeFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Submit your first feedback to get started'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Subject
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Type
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Category
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Priority
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Submitted Date
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedbacks.map((feedback) => {
                    const typeColor = typeColors[feedback.type] || typeColors.feedback;
                    const typeBgColor = typeBgColors[feedback.type] || typeBgColors.feedback;
                    const priorityColor = priorityColors[feedback.priority] || priorityColors.normal;
                    const priorityBgColor = priorityBgColors[feedback.priority] || priorityBgColors.normal;
                    const statusColor = statusColors[feedback.status] || statusColors.open;
                    const statusBgColor = statusBgColors[feedback.status] || statusBgColors.open;

                    return (
                      <tr key={feedback.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 16px', color: '#111827', fontWeight: '500' }}>
                          {feedback.subject}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              backgroundColor: typeBgColor,
                              color: typeColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textTransform: 'capitalize',
                              fontWeight: '500',
                            }}
                          >
                            {feedback.type}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280', textTransform: 'capitalize' }}>
                          {feedback.category}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              backgroundColor: priorityBgColor,
                              color: priorityColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textTransform: 'capitalize',
                              fontWeight: '500',
                            }}
                          >
                            {feedback.priority}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              backgroundColor: statusBgColor,
                              color: statusColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textTransform: 'capitalize',
                              fontWeight: '500',
                            }}
                          >
                            {feedback.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '13px' }}>
                          {feedback.submittedDate ? formatDate(feedback.submittedDate) : '-'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {feedback.status === 'open' && (
                              <button
                                onClick={() => setResolveId(feedback.id)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#10b981',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <CheckCircle size={14} />
                                Resolve
                              </button>
                            )}
                            <button
                              onClick={() => handleEditFeedback(feedback)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#f3f4f6',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(feedback.id)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#fee2e2',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Trash2 size={14} style={{ color: '#dc2626' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Feedback?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this feedback?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFeedback(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setResolveId(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Resolve Feedback
              </h2>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  Resolution Notes
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain how the feedback/complaint has been resolved"
                  rows="5"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setResolveId(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Feedback Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Feedback' : 'Submit Feedback'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveFeedback}>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="feedback">Feedback</option>
                      <option value="complaint">Complaint</option>
                      <option value="suggestion">Suggestion</option>
                      <option value="appreciation">Appreciation</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="academics">Academics</option>
                      <option value="infrastructure">Infrastructure</option>
                      <option value="staff">Staff</option>
                      <option value="food">Food & Nutrition</option>
                      <option value="transport">Transport</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleFormChange}
                    placeholder="Enter feedback subject"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Provide detailed feedback"
                    rows="5"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
